// Sentry frontend integration (BIG-74).
//
// Init order: called from main.jsx BEFORE createRoot. Sentry needs to
// hook the global error/unhandledrejection listeners before any user
// code can throw, otherwise we miss the early-render crashes that are
// the most painful to debug.
//
// Gated on import.meta.env.PROD — localhost stays noise-free, and the
// dev console keeps showing real stack traces instead of being routed
// to Sentry. The DSN is also a build-time constant, so this is fully
// tree-shaken in dev builds.

import * as Sentry from '@sentry/react'

// Field-name patterns we treat as PII and redact in event payloads.
// Substring match, case-insensitive. Tuned to Placed's schema, not just
// generic web defaults — Sentry's built-in scrubbing misses these.
const PII_KEY_PATTERNS = [
  'email', 'phone', 'address', 'street', 'city', 'postal',
  'applicant_', 'foster_', 'adopter_', 'intake_contact_',
  'microchip', 'first_name', 'last_name', 'full_name',
]

// Loose value-shape patterns. Catches PII that ends up in error
// messages or stack frame `vars` even when the field name was
// generic ("payload", "args", etc).
const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g
const PHONE_RE = /\+?\d[\d\s().-]{6,}\d/g

function isPiiKey(key) {
  if (typeof key !== 'string') return false
  const k = key.toLowerCase()
  return PII_KEY_PATTERNS.some(p => k.includes(p))
}

function redactString(s) {
  if (typeof s !== 'string') return s
  return s.replace(EMAIL_RE, '[email]').replace(PHONE_RE, '[phone]')
}

// Recursively walk an event payload and scrub PII. Mutates in place
// for speed — Sentry hands us a fresh object per event so we own it.
// Cycle-safe via a WeakSet: events sometimes contain DOM nodes or
// React fiber refs that self-reference.
function scrubPii(node, seen = new WeakSet()) {
  if (node == null) return node
  if (typeof node === 'string') return redactString(node)
  if (typeof node !== 'object') return node
  if (seen.has(node)) return node
  seen.add(node)
  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i++) node[i] = scrubPii(node[i], seen)
    return node
  }
  for (const key of Object.keys(node)) {
    if (isPiiKey(key)) {
      node[key] = '[redacted]'
    } else {
      node[key] = scrubPii(node[key], seen)
    }
  }
  return node
}

function beforeSend(event) {
  // Drop offline-user errors — PWAs go offline frequently and the
  // resulting "Failed to fetch" / "NetworkError" bursts are pure
  // noise that eats our event quota.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return null

  // Strip query strings from URLs — UUIDs in route params (animal IDs,
  // application refs, claim tokens) are arguably PII / capability tokens.
  if (event.request?.url) {
    try { event.request.url = new URL(event.request.url).origin + new URL(event.request.url).pathname } catch { /* noop */ }
  }

  return scrubPii(event)
}

// SHA-256(user.id + salt) → first 16 hex chars. Pseudonymous identifier
// — consistent across sessions for the same user (so we can see "user
// X hit this 5 times in a row") but not reversible without the salt.
// SubtleCrypto is async; setSentryUser awaits it before calling
// Sentry.setUser, so the helper itself is async too.
async function hashUserId(rawId) {
  const salt = import.meta.env.VITE_SENTRY_USER_SALT || ''
  const enc = new TextEncoder()
  const buf = await crypto.subtle.digest('SHA-256', enc.encode(rawId + salt))
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 16)
}

let initialized = false

export function initSentry() {
  if (initialized) return
  if (!import.meta.env.PROD) return
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return

  Sentry.init({
    dsn,
    // Tag every event with the deploy SHA so the dashboard shows
    // "this error first appeared in <release>" and we can confirm a
    // fix actually shipped. VITE_COMMIT_SHA is set by Netlify.
    release: import.meta.env.VITE_COMMIT_SHA || undefined,
    environment: 'production',

    // Performance monitoring + session replay both off (BIG-74 D5
    // decision — too noisy at HMR scale, eats free-tier budget).
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    beforeSend,

    // Errors that happen frequently and aren't actionable.
    ignoreErrors: [
      // Chromium spec quirk — fires on benign layout, every page.
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Sentry catches these but they almost never have actionable stack frames.
      'Non-Error promise rejection captured',
      // Transient during deploys — old chunk hash invalidated mid-session,
      // resolved by next page load.
      /ChunkLoadError/,
      /Loading chunk \d+ failed/,
      /Failed to fetch dynamically imported module/,
      // Browser extension noise.
      /chrome-extension:\/\//,
      /moz-extension:\/\//,
      /safari-web-extension:\/\//,
    ],

    denyUrls: [
      // Don't loop on Sentry's own outbound requests.
      /sentry\.io/,
      // Anything from a browser extension.
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      /^safari-web-extension:\/\//,
    ],
  })

  initialized = true
}

// Called from auth.jsx after the user_profiles row resolves. The caller
// passes the raw user_id and the resolved profile — we hash the id and
// attach role + org_id as searchable Sentry user context.
export async function setSentryUser({ userId, role, orgId }) {
  if (!initialized) return
  try {
    const id = await hashUserId(userId)
    Sentry.setUser({ id, role: role || null, org_id: orgId || null })
  } catch {
    // Hash failure shouldn't break the app. Just leave the previous user
    // context in place (or no context if first load).
  }
}

export function clearSentryUser() {
  if (!initialized) return
  Sentry.setUser(null)
}

// Re-export a couple of helpers so callers don't need to import @sentry/react
// directly. Keeps the surface area narrow and the imports auditable.
export const captureException = (...args) => initialized ? Sentry.captureException(...args) : undefined
export const captureMessage = (...args) => initialized ? Sentry.captureMessage(...args) : undefined
